import { ApplicationConfig, importProvidersFrom, Injectable, provideZonelessChangeDetection, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { HAMMER_GESTURE_CONFIG, HammerGestureConfig } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { NgcCookieConsentModule, NgcCookieConsentConfig } from 'ngx-cookieconsent';
import { environment } from '../environments/environment';

const routes = [
    { path: '', loadComponent: () => import('./home/home.component').then(m => m.HomeComponent) },
    { path: 'privacy-policy', loadComponent: () => import('./privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent) },
    { path: 'wettelijke-vermeldingen', loadComponent: () => import('./legal-notice/legal-notice.component').then(m => m.LegalNoticeComponent) },
    { path: 'energetische-renovatie', loadComponent: () => import('./energy-renovation/energy-renovation.component').then(m => m.EnergyRenovationComponent) },
    { path: 'gyprocwerken', loadComponent: () => import('./gyproc/gyproc.component').then(m => m.GyprocComponent) },
    { path: 'badkamerrenovatie', loadComponent: () => import('./bathroom/bathroom.component').then(m => m.BathroomComponent) },
    { path: 'zolderrenovatie', loadComponent: () => import('./attic/attic.component').then(m => m.AtticComponent) },
    { path: 'totaalrenovatie', loadComponent: () => import('./total-renovation/total-renovation.component').then(m => m.TotalRenovationComponent) },
    { path: 'renovatie-gent', loadComponent: () => import('./gent/gent.component').then(m => m.GentComponent) },
    { path: '**', loadComponent: () => import('./not-found/not-found.component').then(m => m.NotFoundComponent) }
];

const cookieConfig: NgcCookieConsentConfig = {
    cookie: {},
    palette: {
        popup: { background: '#000000' },
        button: { background: '#ffc107', text: '#000000' }
    },
    theme: 'classic',
    type: 'opt-in',
    content: {
        message: 'Deze website gebruikt cookies om uw ervaring te verbeteren en voor marketingdoeleinden.',
        dismiss: 'Begrepen',
        deny: 'Weigeren',
        link: 'Privacybeleid',
        href: '/privacy-policy',
        policy: 'Privacybeleid',
        allow: 'Accepteren'
    }
};

declare let Hammer: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new(element: HTMLElement, options?: Record<string, unknown>): any;
    SUPPORT_POINTER_EVENTS: boolean;
    PointerEventInput: unknown;
    TouchInput: unknown;
    Swipe: unknown;
};

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
    private platformId = inject(PLATFORM_ID);


    override overrides = {
        pan: { direction: 6 }, // Hammer.DIRECTION_All
        swipe: { direction: 16 }, // Hammer.DIRECTION_VERTICAL
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any;

    override buildHammer(element: HTMLElement) {
        if (isPlatformBrowser(this.platformId) && typeof Hammer !== 'undefined') {
            const mc = new Hammer(element, {
                touchAction: 'auto',
                inputClass: Hammer.SUPPORT_POINTER_EVENTS ? Hammer.PointerEventInput : Hammer.TouchInput,
                recognizers: [
                    [Hammer.Swipe, { direction: 6 }] // Hammer.DIRECTION_HORIZONTAL
                ]
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return mc as any;
        }
        return null;
    }
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideRouter(routes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
                anchorScrolling: 'enabled'
            })
        ),
        provideAnimations(),
        provideHttpClient(withFetch()),
        importProvidersFrom(
            GoogleTagManagerModule.forRoot({ id: environment.gtmId }),
            NgcCookieConsentModule.forRoot(cookieConfig)
        ),
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig
        }
    ]
};
